import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { QueryOptions } from 'utils/useQueryUtils';
import { NetworkChainIds } from '@dapp/multi-lib/network';
import { dynaVaultsAPI } from '../../../../api/DynaVaultsAPI';
import { useVaultBlockchain } from 'structure/pv2/vault/hooks/useVaultBlockchain';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';

interface UseVaultProps extends QueryOptions {
  address: `0x${string}`;
  chainId: number;
}

const STALE_TIME = 60 * 1000;

export const useVault = (options: UseVaultProps) => {
  const { address, chainId, query } = options || {};
  const useQueryOptions = {
    queryKey: queryKeys.vault({ address, chainId }),
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
    ...query,
  };

  const vaultQuery = useQuery({
    retry: 3,
    queryFn: () => dynaVaultsAPI.loadVault({ address, chainId }),
    ...useQueryOptions,
  });

  const isApiError = vaultQuery.isFetched && Boolean(vaultQuery.data) === false;

  const vaultBlockchainQuery = useVaultBlockchain({
    ...options,
    query: {
      enabled: isApiError,
    },
  });

  if (isApiError) {
    return vaultBlockchainQuery;
  }

  return vaultQuery;
};

interface UseVaultAllocationProps extends QueryOptions {
  address: `0x${string}`;
  chainId: number;
}

export const useVaultAllocation = (options: UseVaultAllocationProps) => {
  const { address, chainId, query } = options || {};

  const useQueryOptions = {
    queryKey: queryKeys.vaultAllocation({ chainId, address }),
    staleTime: STALE_TIME,
    ...query,
  };

  return useQuery({
    queryFn: () => dynaVaultsAPI.loadVaultTokenAllocation({ address, chainId }),
    ...useQueryOptions,
  });
};

type UseVaultsStatsParams = QueryOptions & {
  address: `0x${string}`;
  fromTimestamp: number;
  price: boolean;
  tvl: boolean;
  chainId: NetworkChainIds;
};

export const useVaultStats = (options: UseVaultsStatsParams) => {
  const { address, chainId, fromTimestamp, price, tvl, query } = options;

  const useQueryOptions = {
    enabled: true,
    staleTime: STALE_TIME,
    queryKey: queryKeys.vaultStats({
      chainId,
      address,
      fromTimestamp,
      price,
      tvl,
    }),
    ...query,
  };

  return useQuery({
    ...useQueryOptions,
    queryFn: () =>
      dynaVaultsAPI.loadStats({
        address,
        chainId,
        fromTimestamp,
        price,
        tvl,
      }),
  });
};

interface UseVaultTransactionsProps extends QueryOptions {
  address: `0x${string}`;
  chainId: number;
}

export const useVaultTransactions = (options: UseVaultTransactionsProps) => {
  const { address, chainId, query } = options || {};

  const useQueryOptions = {
    queryKey: queryKeys.vaultTransactions({ address, chainId }),
    staleTime: STALE_TIME,
    ...query,
  };

  return useQuery({
    queryFn: () => dynaVaultsAPI.loadVaultTransactions({ address, chainId }),
    ...useQueryOptions,
  });
};

interface UseDecisionReasoningProps extends QueryOptions {
  address: `0x${string}`;
  chainId: number;
}

export const useDecisionReasoning = (options: UseDecisionReasoningProps) => {
  const { address, chainId, query } = options || {};
  const useQueryOptions = {
    queryKey: queryKeys.vaultDecisionReasoning({ address, chainId }),
    staleTime: STALE_TIME,
    ...query,
  };

  return useQuery({
    queryFn: () =>
      dynaVaultsAPI.loadVaultDecisionReasoning({ address, chainId }),
    ...useQueryOptions,
  });
};
